import { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import type { FunctionComponent } from 'react';
import type { ValueOf } from 'type-fest';

import { isActionToast, isLinkToast } from '../components/Toaster/utils';
import { useDebouncedCallback } from '../hooks';
import type { AddToast, NewToast, Toast } from '../components/Toaster/types';

//  Timeout for regular toasts.
const TOAST_TIMEOUT = 3 * 1000; // 3s
//  Timeout for the slide out animation.
const TOAST_ANIMATION_TIMEOUT = 1 * 1000; // 1s

export type ToastContextState = {
  toasts: Toast[];
  /**
   * The primary way to add Toasts. The object provided determines what type of toast will be created.
   *
   * @param {string} toast.type - The type of toast. Supported levels are `info`, `success`, and `error`.
   * @param {string} toast.content - The content to display in the toast.
   * @param {string} [toast.link] - (Optional) A link to an internal FOSSA page. If this is provided, a LinkToast will be rendered.
   * @param {Object} [toast.action] - (Optional) An object describing the action that should happen on Button press. If this is provided, an ActionToast will be rendered.
   *                                  Takes in the `text` for the Button and the `onAction` callback.
   * @returns The `id` of the Toast. Can be used to programatically delete the Toast later with `removeToast`.
   */
  addToast: AddToast;
  //  Removes a toast, visually animating it out before removing it from the DOM.
  hideToast: (id: number) => void;
  clearToasts: () => void;
};

const TOAST_ACTIONS = {
  ADD: 'ADD',
  HIDE: 'HIDE',
  REMOVE: 'REMOVE',
  CLEAR: 'CLEAR',
} as const;

type Action =
  | { type: ValueOf<typeof TOAST_ACTIONS, 'ADD'>; toast: Toast }
  | { type: ValueOf<typeof TOAST_ACTIONS, 'HIDE'>; id: number }
  | { type: ValueOf<typeof TOAST_ACTIONS, 'REMOVE'>; id: number } // Note this action is only used internally and isn't exposed. Only `hide` is exposed.
  | { type: ValueOf<typeof TOAST_ACTIONS, 'CLEAR'>; };

//  Create the context.
export const ToastContext = createContext<ToastContextState>({} as ToastContextState);

/**
 * Hook for the entire Toasts context.
 * NOTE: do NOT use this directly, this should only be consumed by Toaster (the toast manager).
 */
export const useToasts = () => useContext(ToastContext);

/**
 * Hook which allows you to simply add a new toast to the page.
 * @example
 * const MyComponent = () => {
 *   const addToast = useAddToast();
 *
 *   return (
 *     <Button onClick={() => addToast({ content: 'Hello world!' })}>
 *       Add toast!
 *     </Button>
 *   );
 * };
 */
export const useAddToast = (): (toast: NewToast) => void => {
  const { addToast } = useContext(ToastContext);
  return addToast;
};

const reducer = (state: Toast[], action: Action): Toast[] => {
  if (action.type === TOAST_ACTIONS.ADD) {
    return [...state, action.toast];
  }

  //  Visually hide the toast (ie: make it slide out).
  if (action.type === TOAST_ACTIONS.HIDE) {
    return state.map(toast => toast.id !== action.id ? toast : ({
      ...toast,
      isVisible: false,
    }));
  }

  if (action.type === TOAST_ACTIONS.REMOVE) {
    return state.filter(toast => toast.id !== action.id);
  }

  if (action.type === TOAST_ACTIONS.CLEAR) {
    return [];
  }

  return state;
};

const ToastContextProvider: FunctionComponent = ({ children }) => {
  const [toasts, dispatch] = useReducer<typeof reducer>(reducer, []);

  // We debounce this callback to prevent spamming useToast from causing conflicting timestamps.
  const addToast = useDebouncedCallback((newToast: NewToast) => {
    const toast: Toast = {
      ...newToast,
      id: Date.now(),
      isVisible: true,
    };

    //  Add the new toast to the list.
    dispatch({ type: TOAST_ACTIONS.ADD, toast });

    //  Link and Action toasts do not auto-dismiss, but for regular toasts they should
    //  be automatically dismissed after the TOAST_TIMEOUT.
    if (!isLinkToast(toast) && !isActionToast(toast)) {
      setTimeout(() => hideToast(toast.id), TOAST_TIMEOUT);
    }

    return toast.id;
  }, 1);

  const removeToast = useCallback((id: number) => {
    dispatch({ type: TOAST_ACTIONS.REMOVE, id });
  }, []);

  //  This is only used to visually "hide" the toast with an animation.
  //  It then kicks off a timeout to actually remove the toasts from state.
  const hideToast = useCallback((id: number) => {
    dispatch({ type: TOAST_ACTIONS.HIDE, id });
    setTimeout(() => {
      removeToast(id);
    }, TOAST_ANIMATION_TIMEOUT);
  }, [removeToast]);

  const clearToasts = useCallback(() => {
    dispatch({ type: TOAST_ACTIONS.CLEAR });
  }, []);

  const contextValue = useMemo((): ToastContextState => ({
    toasts,
    addToast,
    //  Note that we don't expose the actual removeToast method,
    //  because we want all toasts to be animated out properly.
    hideToast,
    clearToasts,
  }), [addToast, hideToast, clearToasts, toasts]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
};

export default ToastContextProvider;
